/**
 * The HUD: top bar, chat bar with the emote tray, chat log, side panels,
 * player card, toasts and the connection veil. It owns DOM only; the app
 * hands it callbacks and calls `update` when the world changes.
 */
import type { Look } from '../../../shared/cosmetics.ts'
import { CHAT_MAX } from '../../../shared/moderation.ts'
import { ACTS, EMOTES, type Act, type ChatLine, type Emote, type Pose } from '../../../shared/protocol.ts'
import { DEFAULT_ROOM, DISTRICTS, ROOM_IDS, ROOMS, type RoomId } from '../../../shared/world.ts'
import { minimap } from './minimap.ts'
import { BRAND } from '../brand.ts'
import { BUILD } from '../config.ts'
import type { World } from '../game/state.ts'
import { blobArt } from '../render/blob.ts'
import { shade } from '../render/color.ts'
import type { TimeMode } from '../render/scene.ts'
import { h, ICON, iconButton } from './dom.ts'
import { drawThumb, thumb } from './thumb.ts'

export interface Prefs { names: boolean; time: TimeMode; calm: boolean; sound: boolean; zoom: number }

export interface HudActions {
  say(text: string): void
  emote(e: Emote): void
  act(a: Act): void
  pose(p: Pose): void
  wardrobe(): void
  travel(room: RoomId): void
  place(id: string): void
  passport(): { discovered: Set<string> }
  zoom(dir: 1 | -1): void
  prefs(p: Partial<Prefs>): void
  hide(id: string, hidden: boolean): void
  report(id: string): void
  goto(id: string): void
  census(): Promise<{ total: number; rooms: Record<RoomId, { n: number }> } | null>
}

export const EMOTE_KEYS: Record<string, Emote> = { '1': 'happy', '2': 'love', '3': 'surprised', '4': 'sad', '5': 'smug', '6': 'wink', '7': 'shy', '8': 'scared', '9': 'thinking', '0': 'sleepy' }
const ACT_LABEL: Record<Act, string> = { hop: 'Hop', spin: 'Spin', wiggle: 'Wiggle' }

export class Hud {
  readonly input: HTMLInputElement
  private brandFace: HTMLCanvasElement
  private roomLabel: HTMLElement
  private hereCount: HTMLElement
  private clock: HTMLElement
  private tray: HTMLElement
  private faces: HTMLElement
  private log: HTMLElement
  private toasts: HTMLElement
  private panel?: { kind: string; el: HTMLElement }
  private card?: { id: string; el: HTMLElement }
  private veilEl?: HTMLElement
  private lastLook = ''
  private a: HudActions
  prefs: Prefs

  constructor(actions: HudActions, prefs: Prefs) {
    this.a = actions
    this.prefs = prefs
    this.brandFace = h('canvas', { 'aria-hidden': 'true' })
    this.roomLabel = h('small', {}, '…')
    this.hereCount = h('b', {}, '0')
    this.clock = h('span', {}, '')

    const top = h('header', { class: 'top' },
      h('button', { class: 'brand', onclick: () => this.togglePanel('people'), 'aria-label': 'Who is here' }, this.brandFace, h('div', {}, h('b', {}, BRAND), this.roomLabel)),
      h('div', { class: 'stats' },
        h('button', { class: 'sticky yellow', onclick: () => this.togglePanel('people'), 'aria-label': 'Who is here' }, this.hereCount, ' here'),
        h('button', { class: 'sticky blue', onclick: () => this.togglePanel('settings'), 'aria-label': 'Time of day' }, this.clock),
      ),
      h('nav', { class: 'actions' },
        this.labelled(ICON.map, 'Map', () => this.openMap()),
        this.labelled(ICON.hat, 'Wardrobe', () => this.a.wardrobe()),
        iconButton(ICON.minus, 'Zoom out', () => this.a.zoom(-1)),
        iconButton(ICON.plus, 'Zoom in', () => this.a.zoom(1)),
        iconButton(ICON.gear, 'Settings', () => this.togglePanel('settings')),
      ),
    )

    this.faces = h('div', { class: 'faces' })
    this.tray = h('div', { class: 'tray', id: 'tray' },
      h('h3', {}, 'Feelings'), this.faces,
      h('div', { class: 'acts' },
        ...ACTS.map((a) => h('button', { class: 'btn', onclick: () => { this.a.act(a) } }, ACT_LABEL[a])),
        h('button', { class: 'btn', onclick: () => this.a.pose('sit'), title: 'X' }, 'Sit'),
        h('button', { class: 'btn', onclick: () => this.a.pose('nap'), title: 'Z' }, 'Nap'),
        h('button', { class: 'btn', onclick: () => this.a.pose('stand') }, 'Stand'),
      ),
    )
    this.input = h('input', { maxlength: CHAT_MAX, placeholder: 'Say something…  (Enter)', 'aria-label': 'Chat message', enterkeyhint: 'send', autocomplete: 'off' })
    const count = h('span', { class: 'count', 'aria-hidden': 'true' })
    this.input.addEventListener('input', () => { const n = CHAT_MAX - this.input.value.length; count.textContent = n < 30 ? String(n) : '' })
    const sendNow = () => {
      const v = this.input.value.trim()
      if (v) this.a.say(v)
      this.input.value = ''
      count.textContent = ''
    }
    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation()
      if (e.key === 'Enter') { e.preventDefault(); sendNow(); if (!this.input.value) this.input.blur() }
      if (e.key === 'Escape') this.input.blur()
    })
    const trayBtn = iconButton(ICON.smile, 'Feelings and actions', () => this.toggleTray(), 'btn icon ghost')
    trayBtn.setAttribute('aria-controls', 'tray')
    const bottom = h('footer', { class: 'bottom' }, this.tray,
      h('div', { class: 'say' }, trayBtn, iconButton(ICON.chat, 'Chat history', () => this.toggleLog(), 'btn icon ghost'), this.input, count, iconButton(ICON.send, 'Send', sendNow, 'btn icon primary')),
    )
    this.log = h('div', { class: 'log', role: 'log', 'aria-live': 'polite', 'aria-label': 'Chat' })
    this.log.addEventListener('click', () => { if (!this.log.classList.contains('open')) this.toggleLog() })
    this.toasts = h('div', { class: 'toasts', 'aria-live': 'polite' })
    document.body.append(top, bottom, this.log, this.toasts)
    document.addEventListener('pointerdown', (e) => {
      const t = e.target as Node
      if (this.panel && !this.panel.el.contains(t) && !top.contains(t)) this.closePanel()
      if (this.card && !this.card.el.contains(t)) this.closeCard()
      if (this.tray.classList.contains('open') && !bottom.contains(t)) this.toggleTray(false)
    })
  }

  private labelled(icon: string, label: string, onclick: () => void): HTMLButtonElement {
    const b = h('button', { class: 'btn', 'aria-label': label, title: label, onclick: () => onclick() })
    b.innerHTML = `${icon}<span class="label">${label}</span>`
    return b
  }

  // ─── Live bits ──────────────────────────────────────────────────────────

  update(world: World, look: Look | undefined, day: number): void {
    const r = world.room
    // Outdoors, name the place you are at, or else the district you are in.
    let where = r.name
    const me = world.self
    if (me && r.id === DEFAULT_ROOM) {
      const d = world.derive(me, Date.now())
      const district = DISTRICTS.find((q) => d.x >= q.x && d.x < q.x + q.w && d.y >= q.y && d.y < q.y + q.d)
      where = district?.name ?? 'On the road'
      for (const pl of r.places) if (Math.hypot(pl.x - d.x, pl.y - d.y) <= (pl.r ?? 6) * 0.6) where = pl.name
    }
    this.roomLabel.textContent = `${where}${world.shard > 1 ? ` #${world.shard}` : ''} · ${world.players.size} here`
    this.hereCount.textContent = String(world.players.size)
    const d = new Date()
    this.clock.textContent = `${day > 0.5 ? '☀' : '☾'} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}${this.prefs.time === 'auto' ? '' : ` · ${this.prefs.time}`}`
    const key = look ? JSON.stringify(look) : ''
    if (look && key !== this.lastLook) {
      this.lastLook = key
      drawThumb(this.brandFace, look)
      this.faces.replaceChildren(...EMOTES.map((e) => {
        const k = Object.keys(EMOTE_KEYS).find((x) => EMOTE_KEYS[x] === e)
        return h('button', { onclick: () => { this.a.emote(e); this.toggleTray(false) }, 'aria-label': e }, thumb(look, 40, e, false), e, k ? h('kbd', {}, k) : null)
      }))
    }
    if (this.panel?.kind === 'people') this.renderPeople(world)
  }

  chat(line: ChatLine, world: World, sys = false): void {
    const p = world.players.get(line.id)
    const color = p ? blobArt(p.look).color : '#8a84a3'
    const el = h('p', { class: sys ? 'sys' : '' }, sys ? null : h('b', { style: `color:${shade(color, -0.45)}` }, line.name), line.text)
    this.log.append(el)
    while (this.log.children.length > 80) this.log.firstElementChild?.remove()
    if (this.log.classList.contains('open')) this.log.scrollTop = this.log.scrollHeight
  }

  resetLog(world: World): void {
    this.log.replaceChildren()
    for (const l of world.chat.slice(-30)) this.chat(l, world)
  }

  toast(text: string, ms = 3200): void {
    const t = h('div', { class: 'toast' }, text)
    this.toasts.append(t)
    setTimeout(() => t.remove(), ms)
  }

  // ─── Tray and log ───────────────────────────────────────────────────────

  toggleTray(open = !this.tray.classList.contains('open')): void {
    this.tray.classList.toggle('open', open)
  }

  toggleLog(open = !this.log.classList.contains('open')): void {
    this.log.classList.toggle('open', open)
    if (open) this.log.scrollTop = this.log.scrollHeight
  }

  get typing(): boolean { return document.activeElement === this.input }

  closeAll(): boolean {
    const had = !!(this.panel || this.card || this.tray.classList.contains('open') || this.log.classList.contains('open'))
    this.closePanel(); this.closeCard(); this.toggleTray(false); this.toggleLog(false)
    return had
  }

  // ─── Panels ─────────────────────────────────────────────────────────────

  private world?: World
  bind(world: World): void { this.world = world }

  togglePanel(kind: 'people' | 'settings'): void {
    if (this.panel?.kind === kind) { this.closePanel(); return }
    this.closePanel()
    const el = h('aside', { class: 'panel', role: 'dialog', 'aria-label': kind === 'people' ? 'Who is here' : 'Settings' })
    this.panel = { kind, el }
    document.body.append(el)
    if (kind === 'people' && this.world) this.renderPeople(this.world)
    if (kind === 'settings') this.renderSettings()
  }

  closePanel(): void { this.panel?.el.remove(); this.panel = undefined }

  private renderPeople(world: World): void {
    const el = this.panel!.el
    const players = [...world.players.values()].sort((a, b) => (a.id === world.me ? -1 : b.id === world.me ? 1 : a.name.localeCompare(b.name)))
    el.replaceChildren(
      h('h2', {}, `${world.room.name} · ${players.length} here`),
      ...players.map((p) => h('div', { class: 'row', role: 'button', tabindex: 0, onclick: (e: Event) => { if (p.id !== world.me) this.openCard(p.id, (e as MouseEvent).clientX, (e as MouseEvent).clientY) } },
        thumb(p.look, 34), h('span', {}, p.name), p.id === world.me ? h('small', { class: 'you' }, 'you') : world.hidden.has(p.id) ? h('small', { class: 'you' }, 'hidden') : null)),
      ...(players.length < 2 ? [h('p', { class: 'hint' }, 'Just you for now. Rooms fill up through the evening; the map shows where everyone is.')] : []),
    )
  }

  private renderSettings(): void {
    const el = this.panel!.el
    const sw = (label: string, key: 'names' | 'calm' | 'sound') => {
      const input = h('input', { type: 'checkbox', checked: this.prefs[key], onchange: () => { this.a.prefs({ [key]: input.checked }) } })
      return h('label', { class: 'opt' }, label, h('span', { class: 'switch' }, input, h('span', {})))
    }
    const seg = h('div', { class: 'seg', role: 'group', 'aria-label': 'Time of day' }, ...(['auto', 'day', 'night'] as const).map((m) =>
      h('button', { 'aria-pressed': String(this.prefs.time === m), onclick: () => { this.a.prefs({ time: m }); this.renderSettings() } }, m === 'auto' ? 'Local' : m === 'day' ? 'Day' : 'Night')))
    el.replaceChildren(
      h('h2', {}, 'Settings'),
      sw('Always show every name', 'names'),
      h('label', { class: 'opt' }, 'Time of day', seg),
      sw('Calmer motion', 'calm'),
      sw('Sounds', 'sound'),
      h('p', { class: 'hint', html: '<b>Walk</b> click a tile, or WASD / arrows · <b>Chat</b> Enter · <b>Feelings</b> 1–0 · <b>Hop</b> Space · <b>Sit</b> X · <b>Nap</b> Z · <b>Map</b> M' }),
      h('p', { class: 'hint' }, `Everyone here is a guest. Nothing about you is kept after you leave, except the last few lines of chat while a room is busy. Click a blob to hide or report them. Build ${BUILD.slice(0, 7)}.`),
    )
  }

  // ─── Player card ────────────────────────────────────────────────────────

  openCard(id: string, x: number, y: number): void {
    const world = this.world
    const p = world?.players.get(id)
    if (!world || !p || id === world.me) return
    this.closeCard()
    const hidden = world.hidden.has(id)
    const el = h('div', { class: 'card', role: 'dialog', 'aria-label': p.name },
      h('header', {}, thumb(p.look, 46), h('div', {}, h('b', {}, p.name), h('small', {}, hidden ? 'hidden for you' : world.room.name))),
      h('div', { class: 'btns' },
        h('button', { class: 'btn', onclick: () => { this.a.goto(id); this.closeCard() } }, 'Walk over'),
        h('button', { class: 'btn', onclick: () => { this.a.act('hop'); this.a.emote('happy'); this.closeCard() } }, 'Say hi'),
        h('button', { class: 'btn', onclick: () => { this.a.hide(id, !hidden); this.closeCard() } }, hidden ? 'Show' : 'Hide'),
        h('button', { class: 'btn warn', onclick: () => { if (confirm(`Report ${p.name}? They will be hidden for you, and repeated reports mute them for everyone.`)) { this.a.report(id); this.closeCard() } } }, 'Report'),
      ),
    )
    document.body.append(el)
    const r = el.getBoundingClientRect()
    el.style.left = `${Math.max(8, Math.min(window.innerWidth - r.width - 8, x - r.width / 2))}px`
    el.style.top = `${Math.max(64, Math.min(window.innerHeight - r.height - 90, y - r.height - 24))}px`
    this.card = { id, el }
  }

  closeCard(): void { this.card?.el.remove(); this.card = undefined }

  // ─── Map ────────────────────────────────────────────────────────────────

  /** The map: the town drawn small with pins for every place (found or not), your passport, and the indoors. */
  async openMap(opts: { tram?: boolean } = {}): Promise<void> {
    const world = this.world
    this.closeAll()
    const { discovered } = this.a.passport()
    const me = world?.self && world.room.id === DEFAULT_ROOM ? world.derive(world.self, Date.now()) : undefined
    const places = ROOMS[DEFAULT_ROOM].places
    const indoors = h('div', { class: 'rooms' })
    const count = (n: number | undefined) => (n === undefined ? '…' : n === 0 ? 'Quiet' : `${n} inside`)
    const fill = (c: Record<string, { n: number }> | undefined) => indoors.replaceChildren(...ROOM_IDS.filter((id) => id !== DEFAULT_ROOM).map((id) => {
      const r = ROOMS[id]
      const here = world?.room.id === id
      const n = here ? world!.players.size : c?.[id]?.n
      return h('button', { class: `room${here ? ' here' : ''}`, style: `--c:${r.accent}`, onclick: () => { close(); if (!here) this.a.travel(id) } },
        h('b', {}, r.name), h('span', { class: 'n' }, here ? 'You are here' : count(n)))
    }))
    fill(undefined)
    const total = h('span', {}, '')
    const width = Math.min(560, window.innerWidth - (window.innerWidth > 760 ? 330 : 70))
    const map = minimap({ width, me, discovered, onPlace: (pl) => { close(); this.a.place(pl.id) } })
    const got = places.filter((p) => discovered.has(p.id)).length
    const passport = h('div', { class: 'passport' },
      h('h3', {}, `Passport · ${got}/${places.length}`),
      h('div', { class: 'stamps' }, ...places.map((p) => h('div', { class: `stamp${discovered.has(p.id) ? ' got' : ''}`, style: `--c:${p.accent}`, title: discovered.has(p.id) ? p.name : 'Not found yet' }, discovered.has(p.id) ? p.name : '?'))))
    const dialog = h('div', { class: 'dialog', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'map-title', style: 'width:min(860px,100%)' },
      h('h1', { id: 'map-title' }, opts.tram ? 'All aboard!' : 'Blobland'),
      h('p', { class: 'sub' }, opts.tram ? 'Pick a stop. The tram goes anywhere you have already found. ' : 'Walk anywhere. Places you have found can be reached in a hop; the ? ones are waiting. ', total),
      h('div', { class: 'mapgrid' }, map, h('div', { class: 'mapside' }, passport, h('h3', { class: 'maph' }, 'Indoors'), indoors)),
      h('div', { class: 'foot' }, h('button', { class: 'btn ghost', onclick: () => close() }, 'Close')))
    const scrim = h('div', { class: 'scrim' }, dialog)
    const close = () => { scrim.remove(); document.removeEventListener('keydown', onKey) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    scrim.addEventListener('pointerdown', (e) => { if (e.target === scrim) close() })
    document.addEventListener('keydown', onKey)
    document.body.append(scrim)
    const census = await this.a.census()
    if (census && scrim.isConnected) {
      fill(census.rooms)
      total.textContent = `${census.total} ${census.total === 1 ? 'blob' : 'blobs'} around town.`
    }
  }

  // ─── Veil ───────────────────────────────────────────────────────────────

  veil(v: { title: string; text: string; look?: Look; button?: { label: string; run: () => void } } | null): void {
    this.veilEl?.remove()
    this.veilEl = undefined
    if (!v) return
    const btn = v.button ? h('button', { class: 'btn primary', onclick: () => v.button!.run() }, v.button.label) : null
    this.veilEl = h('div', { class: 'veil', role: 'alertdialog', 'aria-live': 'assertive' },
      h('div', { class: 'box' }, v.look ? thumb(v.look, 96, v.button ? 'sleepy' : 'unsure') : null, h('h2', {}, v.title), h('p', {}, v.text), btn))
    document.body.append(this.veilEl)
    btn?.focus()
  }
}

